import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import CounterOfferModal from "../ui/modals/CounterOfferModal";
import { createCounterOffer, approveCounterOffer, rejectCounterOffer } from "../../services/userService";
import { showToast } from "../../utils/Toast";

const calculateRemainingTime = (createdAt) => {
  if (!createdAt) return null;
  
  const created = new Date(createdAt);
  const expiry = new Date(created.getTime() + 24 * 60 * 60 * 1000); // +24 hours
  const now = new Date();
  const remaining = expiry - now;
  
  if (remaining <= 0) return null; // Expired
  
  const hours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((remaining % (1000 * 60)) / 1000);
  
  return { hours, minutes, seconds, totalMs: remaining };
};


// Order Card Component with Action Dropdown
const OrderCardWithAction = ({ order, isPaid, needsRefresh, setNeedsRefresh }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [counterOfferAmount, setCounterOfferAmount] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [remainingTime, setRemainingTime] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Update remaining time every second
  useEffect(() => {
    if (order?.counterOffer?.createdAt) {
      const updateTimer = () => {
        const time = calculateRemainingTime(order.counterOffer.createdAt);
        setRemainingTime(time);
      };
      
      updateTimer(); // Initial update
      const interval = setInterval(updateTimer, 1000);
      
      return () => clearInterval(interval);
    } else {
      setRemainingTime(null);
    }
  }, [order?.counterOffer?.createdAt]);

  // Check if counter offer has expired
  const isCounterOfferExpired = () => {
    if (!order?.counterOffer?.createdAt) return false;
    const time = calculateRemainingTime(order.counterOffer.createdAt);
    return time === null;
    };

  const getStatusBgColor = (status, orderStatus) => {
  if (status === "Sold On Approval") return "bg-orange-500"; 
  if (status === "Won" || status === "Sold" || orderStatus === "Won") return "bg-green-600";

    switch (status) {
      case "Shipping":
        return "bg-yellow-500";
      case "Cancelled":
        return "bg-red-500";
      case "Completed":
        return "bg-green-500";
      default:
        return "bg-blue-500";
    }
  };

  const handleActionSelect = async (action) => {
    if (action === "Countered") {
      setIsModalOpen(true);
      setIsDropdownOpen(false);
    } else if (action === "Accepted") {
      await handleAcceptCounterOffer();
    } else if (action === "Rejected") {
      await handleRejectCounterOffer();
    } else {
      setIsDropdownOpen(false);
      console.log(`Action selected for car ${order?.id}: ${action}`);
    }
  };

  const handleAcceptCounterOffer = async () => {
    try {
      if (!order?.counterOffer?.id) {
        showToast("No counter offer found to accept", "error");
        return;
      }
      const response = await approveCounterOffer(order.counterOffer.id);
      setIsDropdownOpen(false);
      showToast("Counter offer accepted successfully!", "success");
      
      // Refresh the data
      if (setNeedsRefresh) {
        setNeedsRefresh(true);
      }
      
      console.log("Counter offer accepted:", response);
    } catch (error) {
      console.error("Error accepting counter offer:", error);
      
      if (error.response?.data?.message) {
        showToast(`Error: ${error.response.data.message}`, "error");
      } else {
        showToast("Failed to accept counter offer. Please try again.", "error");
      }
    }
  };

  const handleRejectCounterOffer = async () => {
    try {
      if (!order?.counterOffer?.id) {
        showToast("No counter offer found to reject", "error");
        return;
      }

      const response = await rejectCounterOffer(order.counterOffer.id);
      
      setIsDropdownOpen(false);
      showToast("Counter offer rejected successfully!", "success");
      
      // Refresh the data
      if (setNeedsRefresh) {
        setNeedsRefresh(true);
      }
      
      console.log("Counter offer rejected:", response);
    } catch (error) {
      console.error("Error rejecting counter offer:", error);
      
      if (error.response?.data?.message) {
        showToast(`Error: ${error.response.data.message}`, "error");
      } else {
        showToast("Failed to reject counter offer. Please try again.", "error");
      }
    }
  };

  const toggleDropdown = (e) => {
    // Kept for future use when inline dropdown is restored
    e.preventDefault();
    e.stopPropagation();
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setCounterOfferAmount("");
  };

  const handleCounterOfferSubmit = async () => {
    if (counterOfferAmount && !isNaN(Number(counterOfferAmount))) {
      setIsSubmitting(true);
      try {
        const minAllowed = Number(order?.counterOffer?.counterOfferPrice) || 0;
        const numeric = Number(counterOfferAmount);
        if (numeric < minAllowed) {
          showToast(`Counter offer must be at least ${minAllowed.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`, "error");
          setIsSubmitting(false);
          return;
        }
        const sender = "buyer"; 
        const counterOfferData = {
          carID: order?.id,
          counterOfferPrice: Number(counterOfferAmount),
          sender: sender,
          carTitle: order?.title,
        };
        
        await createCounterOffer(counterOfferData);
        setNeedsRefresh(true);
        setIsModalOpen(false);
        console.log(`Counter offer submitted for car ${order?.id}: $${counterOfferAmount}`);
        setCounterOfferAmount("");
        showToast("Counter offer submitted successfully!", "success");
        // redirect buyer after countering
        try {
          navigate('/user/account/order/local?tab=local&paid=not_paid');
        } catch {}
        
      } catch (error) {
        console.error("Error submitting counter offer:", error);
        
        if (error.response?.data?.message === "Not your turn") {
          showToast("It's not your turn to make a counter offer. Please wait for the other party to respond.", "warning");
        } else if (error.response?.data?.message) {
          showToast(`Error: ${error.response.data.message}`, "error");
        } else {
          showToast("Failed to submit counter offer. Please try again.", "error");
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const isOnReserve = order?.status === "Sold On Approval";

    return (
    <>
      <Link to={`/local-vehicle-detail/${order?.id}`} state={{ winningBid: order?.winningBid }} className="block">
      <div className="flex flex-col lg:flex-row border justify-between mx-auto lg:min-h-[10vw] p-4 lg:p-[1vw] rounded-xl lg:rounded-[0.75vw] shadow-sm mb-4 lg:mb-[1vw] cursor-pointer">
        <div className="block lg:hidden h-full lg-mt-[2.03vw] lg:h-[12vh] w-[90%] mx-auto lg:w-[6vw] rounded-xl lg:rounded-[0.75vw]">
          <img
            src={order?.carImages?.[0] || "N/A"}
            alt="order_image"
            className="h-full w-full object-cover rounded-xl lg:rounded-[0.75vw]"
          />
        </div>

        <div className="flex justify-center items-center w-full">
          <div className="flex gap-3 lg:gap-[0.75vw] font-urbanist w-full">
            <div className="w-full">
              <div className="flex flex-col lg:flex-row lg:flex-wrap justify-start items-center gap-2 lg:gap-[1.25vw]">
                <p className="text-[18px] lg:text-[1.2vw] font-semibold">
                  {order?.title || "N/A"}
                </p>
                <div className="text-[14px] lg:text-[0.9vw] font-semibold flex flex-wrap items-center gap-2">
                  <span className="text-gray-600 font-normal">Bid Price:</span>
                  <span>
                    {typeof order?.orderPrice === 'number'
                      ? order.orderPrice.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                      : (typeof order?.currentBid === 'number'
                          ? order.currentBid.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                          : 'N/A')}
                  </span>
                  <span className="mx-2 hidden lg:inline">|</span>
                </div>

                <div className="text-[14px] lg:text-[0.9vw] font-semibold flex flex-wrap items-center gap-2">
                  <span className="text-gray-600 font-normal">Winning Bid:</span>
                  <span>
                    {(() => {
                      const numeric = Number(order?.winningBid);
                      return !isNaN(numeric) && order?.winningBid !== undefined && order?.winningBid !== null && order?.winningBid !== ""
                        ? numeric.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                        : '__';
                    })()}
                  </span>
                  {isPaid && (
                    <div className="ml-2 flex items-center justify-center px-3 lg:px-[0.6vw] py-[2px] rounded-full text-white text-[12px] lg:text-[0.8vw] bg-green-600">
                      Paid
                    </div>
                  )}
                </div>
                {order?.counterOffer && (
                <div className="text-[14px] lg:text-[0.9vw] font-semibold flex flex-wrap items-center gap-2">
                    <span className="text-gray-600 font-normal">Offer Price:</span>
                    <span className="text-green-600 font-bold">
                      {Number(order.counterOffer.counterOfferPrice).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                  </span>
                  <span className="text-gray-500 text-xs">
                  ({order.counterOffer.sender === 'seller' ? 'From Seller' : 'From Buyer'})
                  </span>
                </div>
                )}
                {remainingTime && (
                  <div className="text-[12px] lg:text-[0.8vw] font-semibold flex items-center gap-1">
                    <span className="text-red-600">⏰</span>
                    <span className="text-red-600">
                      {remainingTime.hours}h {remainingTime.minutes}m {remainingTime.seconds}s left
                    </span>
                  </div>
                )}
              </div>

              <div className="flex flex-col lg:flex-row justify-start items-center gap-5 lg:gap-[1.25vw]">
                <div className="lg:block hidden h-[80px] lg-mt-[40px] lg:h-[12vh] w-[80px] lg:w-[6vw] rounded-xl lg:rounded-[0.75vw]">
                  <img
                    src={order?.carImages?.[0] || "N/A"}
                    alt="order_image"
                    className="h-full w-full object-cover rounded-xl lg:rounded-[0.75vw]"
                  />
                </div>

                <div className="gap-2 lg:gap-[0.5vw] text-center lg:text-left font-urbanist text-[1vw]">
                  <p className="text-[16px] lg:text-[1vw] text-[#7a798a]">
                    {order?.carLocation}
                  </p>

                  <p className="text-[16px] lg:text-[1vw] text-[#7a798a]">
                    {`Updated At: ${require('moment-timezone')(order.updatedAt).format("DD-MM-YYYY HH:mm")}`}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-center items-center">
          <div className={`flex items-center gap-2`}>
            <div
                className={`flex items-center justify-center px-4 lg:px-[1vw] py-1 cursor-pointer rounded-full text-white text-[15px] lg:text-[1vw] whitespace-nowrap ${getStatusBgColor(
                order.status || order.orderStatus || "N/A"
              )}`}
            >
              {order.status === "Sold On Approval" ? "On Reserve" : (order.status === "Sold" ? "Won" : order.status) || order.orderStatus || "N/A"}
            </div>
              
              {/* Action Dropdown - Show based on counter offer status */}
              {isOnReserve && !isCounterOfferExpired() && (
                <div className="relative" ref={dropdownRef}>
                  {/* Check if there's a counter offer and who made it */}
                  {order?.counterOffer && order?.counterOffer?.sender === 'buyer' ? (
                    // If buyer made counter offer previously, do not show a redundant pill
                    <div className="hidden" />
                  ) : order?.counterOffer && order?.counterOffer?.sender === 'seller' ? (
                    // If seller made counter offer, show "Action" button for buyer to respond
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const bidPrice = typeof order?.orderPrice === 'number'
                          ? order.orderPrice
                          : (typeof order?.currentBid === 'number' ? order.currentBid : null);
                        const offerPrice = order?.counterOffer?.counterOfferPrice ?? null;
                        const counterOfferId = order?.counterOffer?.id ?? null;
                        navigate(`/local-vehicle-detail/${order?.id}` , {
                          state: {
                            winningBid: order?.winningBid,
                            bidPrice,
                            offerPrice,
                            counterOfferId,
                          }
                        });
                      }}
                      className="px-4 lg:px-[1vw] py-1 text-sm bg-blue-500 text-white rounded-full hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 text-[15px] lg:text-[1vw] whitespace-nowrap"
                    >
                      {"Review Offer"}
                    </button>
                  ) : (
                    // No counter offer yet, show "Action" button
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const bidPrice = typeof order?.orderPrice === 'number'
                          ? order.orderPrice
                          : (typeof order?.currentBid === 'number' ? order.currentBid : null);
                        const offerPrice = order?.counterOffer?.counterOfferPrice ?? null;
                        const counterOfferId = order?.counterOffer?.id ?? null;
                        navigate(`/local-vehicle-detail/${order?.id}` , {
                          state: {
                            winningBid: order?.winningBid,
                            bidPrice,
                            offerPrice,
                            counterOfferId,
                          }
                        });
                      }}
                      className="px-4 lg:px-[1vw] py-2 text-sm bg-blue-500 text-white rounded-full hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 text-[15px] lg:text-[1vw] whitespace-nowrap"
                    >
                      {"Review Offer"}
                    </button>
                  )}
                  {/* Only show dropdown if it's a clickable button (not "Countered" status) */}
                  {false && isDropdownOpen && !(order?.counterOffer && order?.counterOffer?.sender === 'buyer') && (
                    <div className="absolute top-full right-0 mt-1 w-32 bg-white border border-gray-300 rounded-md shadow-lg z-10">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleActionSelect("Accepted");
                        }}
                        className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Accepted
                      </button>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleActionSelect("Rejected");
                        }}
                        className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Rejected
                      </button>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleActionSelect("Countered");
                        }}
                        className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Countered
                      </button>
                    </div>
                  )}
                </div>
              )}
              {isOnReserve && isCounterOfferExpired() && order?.counterOffer && (
                <div className="px-4 lg:px-[1vw] py-1 text-sm bg-red-500 text-white rounded-full text-[15px] lg:text-[1vw]">
                  Timeout
                </div>
              )}
          </div>
        </div>
      </div>
      </Link>

      {/* Counter Offer Modal */}
      <CounterOfferModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleCounterOfferSubmit}
        amount={counterOfferAmount}
        setAmount={setCounterOfferAmount}
        carTitle={order?.title || "N/A"}
        minAmount={Number(order?.counterOffer?.counterOfferPrice) || 0}
        isLoading={isSubmitting}
      />
    </>
  );
};

const LocalUsersOrder = ({ userOrders, paidOrders = [], needsRefresh, setNeedsRefresh }) => {
  const getStatusBgColor = (status, orderStatus) => {
  if (status === "Sold On Approval") return "bg-orange-500"; 
  if (status === "Won" || status === "Sold" || orderStatus === "Won") return "bg-green-600";

    switch (status) {
      case "Shipping":
        return "bg-yellow-500";
      case "Cancelled":
        return "bg-red-500";
      case "Completed":
        return "bg-green-500";
      default:
        return "bg-blue-500";
    }
  };


  return (
    <div className="w-full mx-auto text-black">
      {userOrders && userOrders.map((order, index) => (
        <div key={`uo-${index}`}>
          <OrderCardWithAction order={order} isPaid={false} needsRefresh={needsRefresh} setNeedsRefresh={setNeedsRefresh} />
        </div>
      ))}

      {paidOrders && paidOrders.length > 0 && (
        <div className="mt-6 border-t pt-4">
          <p className="text-[18px] lg:text-[1.2vw] font-semibold mb-4">Paid Local Cars</p>
          {paidOrders.map((order, index) => (
            <div key={`po-${index}`}>
              <OrderCardWithAction order={order} isPaid={true} needsRefresh={needsRefresh} setNeedsRefresh={setNeedsRefresh} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocalUsersOrder; 